import {
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Stack,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useUpdate360 } from "../Hooks/useUpdate360";
import { useResearchTasks } from "../../common/contexts/ResearchTaskProvider";
import ResearchTask from "../Components/ResearchTask";

const Update360Page = () => {
  const {
    personId,
    loading,
    error,
    handleSubmit,
    handleInputChange,
    agree360,
    setAgree360,
    enoughDataAgreement,
    setEnoughDataAgreement,
    autoInviteToCircle,
    setAutoInviteToCircle,
  } = useUpdate360();

  const { tasksMap } = useResearchTasks();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Update 360
      </Typography>
      <Typography variant="body1" component="h1" gutterBottom>
        Enter the NATIONBUILDER ID to perform 360 and update their profile.
      </Typography>

      <Box sx={{ maxWidth: 400, mt: 3 }}>
        <TextField
          fullWidth
          label="NATIONBUILDER ID"
          value={personId}
          onChange={handleInputChange}
          placeholder="Enter NATIONBUILDER ID"
          type="text"
          slotProps={{
            input: {
              inputMode: "numeric",
            },
            htmlInput: { maxLength: 12 },
          }}
          margin="normal"
        />

        <Stack spacing={2} sx={{ mt: 2, alignItems: "flex-start" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agree360}
                onChange={(e) => setAgree360(e.target.checked)}
              />
            }
            label="I confirm that this user has provided verbal or written acceptance of the 360 profile search"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={enoughDataAgreement}
                onChange={(e) => setEnoughDataAgreement(e.target.checked)}
              />
            }
            label="Please ensure that the person has provided sufficient information to enable a complete and accurate profile search"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={autoInviteToCircle}
                onChange={(e) => setAutoInviteToCircle(e.target.checked)}
              />
            }
            label="Automatically Invite to Circle when done"
          />
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !personId.trim() || loading || !agree360 || !enoughDataAgreement
            }
          >
            {loading ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mt: 2,
        }}
      >
        {Array.from(tasksMap.values())
          .reduce<React.ReactNode[][]>((rows, task, idx) => {
            if (idx % 3 === 0) rows.push([]);
            rows[rows.length - 1].push(
              <Box
                key={`${task.id}-${task.created_at}`}
                sx={{ minWidth: 300, flex: "1 0 300px" }}
              >
                <ResearchTask
                  personId={task.id}
                  taskResult={task.task_result ?? null}
                />
              </Box>,
            );
            return rows;
          }, [])
          .map((row, rowIdx) => (
            <Box
              key={rowIdx}
              sx={{
                display: "flex",
                gap: 2,
                width: "100%",
                mb: 2,
              }}
            >
              {row}
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default Update360Page;
